import React, { useMemo } from 'react';

const ROUND_ORDER = [
    'Pre-Season',
    ...Array.from({ length: 29 }, (_, i) => `Round ${i}`),
];

function roundSortKey(round) {
    const idx = ROUND_ORDER.indexOf(round);
    return idx >= 0 ? idx : 999;
}

function RoundBreakdown({ bets, displayMode = 'dollars', unitSize = 25, getUnitSizeForDate, advancedUnitSettings = false }) {
    const aflBets = useMemo(() => bets.filter(b => b.sport === 'afl'), [bets]);
    const isUnits = displayMode === 'units';

    const { roundData, totals } = useMemo(() => {
        const grouped = {};

        aflBets.forEach(bet => {
            const round = bet.round || 'Unknown';
            const unitDivisor = advancedUnitSettings && getUnitSizeForDate ? getUnitSizeForDate(bet.date) : unitSize;
            const displayDivisor = isUnits ? unitDivisor : 1;
            if (!grouped[round]) {
                grouped[round] = { round, wins: 0, losses: 0, cashouts: 0, pending: 0, staked: 0, returned: 0, pl: 0, plUnits: 0 };
            }
            const g = grouped[round];

            if (bet.result === 'win') g.wins++;
            else if (bet.result === 'loss') g.losses++;
            else if (bet.result === 'cash out') g.cashouts++;
            else if (bet.result === 'pending') g.pending++;

            if (!bet.bonus_bet) g.staked += bet.stake / displayDivisor;
            g.pl += bet.pl / displayDivisor;
            g.plUnits += bet.pl / unitDivisor;
            if (bet.result !== 'pending') {
                g.returned += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / displayDivisor;
            }
        });

        const rows = Object.values(grouped).sort((a, b) => roundSortKey(a.round) - roundSortKey(b.round));

        const totals = rows.reduce(
            (acc, r) => ({
                wins: acc.wins + r.wins,
                losses: acc.losses + r.losses,
                cashouts: acc.cashouts + r.cashouts,
                pending: acc.pending + r.pending,
                staked: acc.staked + r.staked,
                returned: acc.returned + r.returned,
                pl: acc.pl + r.pl,
                plUnits: acc.plUnits + r.plUnits,
            }),
            { wins: 0, losses: 0, cashouts: 0, pending: 0, staked: 0, returned: 0, pl: 0, plUnits: 0 }
        );

        return { roundData: rows, totals };
    }, [aflBets, isUnits, unitSize, advancedUnitSettings, getUnitSizeForDate]);

    if (aflBets.length === 0) return null;

    const fmtCurrency = (v) => isUnits ? `${v.toFixed(1)}U` : `$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const fmtPL = (v) => isUnits ? `${v >= 0 ? '+' : ''}${v.toFixed(1)}U` : `${v >= 0 ? '+' : '-'}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const fmtROI = (staked, pl) => staked > 0 ? `${pl >= 0 ? '+' : ''}${((pl / staked) * 100).toFixed(1)}%` : '-';
    const plColor = (v) => v > 0 ? 'text-emerald-400' : v < 0 ? 'text-red-400' : 'text-slate-400';

    const columns = [
        { key: 'round', label: 'Round', align: 'left' },
        { key: 'wins', label: 'Wins', align: 'center' },
        { key: 'losses', label: 'Losses', align: 'center' },
        { key: 'cashouts', label: 'Cashouts', align: 'center' },
        { key: 'pending', label: 'Pending', align: 'center' },
        { key: 'winRate', label: 'Win %', align: 'center' },
        { key: 'staked', label: 'Staked', align: 'left' },
        { key: 'returned', label: 'Returned', align: 'left' },
        { key: 'pl', label: 'P/L', align: 'left' },
        { key: 'plUnits', label: 'Units', align: 'left' },
        { key: 'roi', label: 'ROI', align: 'left' },
    ];

    const renderRow = (row, isTotal = false) => {
        const cellClass = `px-3 py-2 text-[11px] font-mono tabular-nums whitespace-nowrap ${isTotal ? 'font-semibold' : ''}`;

        return (
            <tr
                key={isTotal ? 'total' : row.round}
                className={isTotal ? 'bg-slate-800/60 border-t border-slate-700' : 'hover:bg-slate-800/40 transition-colors'}
            >
                <td className={`${cellClass} ${isTotal ? 'text-white font-bold uppercase tracking-wider' : 'text-white font-medium'}`}>
                    {isTotal ? '— TOTAL' : row.round}
                </td>
                <td className={`${cellClass} text-center text-emerald-400`}>{row.wins}</td>
                <td className={`${cellClass} text-center text-red-400`}>{row.losses}</td>
                <td className={`${cellClass} text-center text-blue-400`}>{row.cashouts}</td>
                <td className={`${cellClass} text-center text-amber-400`}>{row.pending}</td>
                <td className={`${cellClass} text-center text-slate-300`}>
                    {(row.wins + row.losses) > 0 ? `${((row.wins / (row.wins + row.losses)) * 100).toFixed(1)}%` : '—'}
                </td>
                <td className={`${cellClass} text-slate-300`}>{fmtCurrency(row.staked)}</td>
                <td className={`${cellClass} text-slate-300`}>{fmtCurrency(row.returned)}</td>
                <td className={`${cellClass} font-semibold ${plColor(row.pl)}`}>{fmtPL(row.pl)}</td>
                <td className={`${cellClass} font-semibold ${plColor(row.plUnits)}`}>{row.plUnits >= 0 ? '+' : ''}{row.plUnits.toFixed(2)}U</td>
                <td className={`${cellClass} font-semibold ${fmtROI(row.staked, row.pl) !== '-' ? plColor(row.pl) : 'text-slate-500'}`}>
                    {fmtROI(row.staked, row.pl)}
                </td>
            </tr>
        );
    };

    return (
        <div className="bg-[#0f172a] rounded border border-slate-700/50 overflow-hidden">
            {/* Command Bar Header */}
            <div className="flex items-center justify-between px-3 md:px-4 py-2.5 border-b border-slate-700/50 bg-slate-800/30">
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <div className={`w-1 h-4 ${totals.pl >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <h3 className="text-xs md:text-sm font-semibold text-white tracking-tight whitespace-nowrap">AFL Round Performance</h3>
                    <span className="hidden md:inline text-[10px] font-mono text-slate-600 tracking-widest uppercase">{roundData.length} rounds</span>
                </div>
                <div className={`text-base md:text-lg font-bold font-mono tabular-nums ${totals.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {fmtPL(totals.pl)}
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-800/30 border-b border-slate-700/50">
                        <tr>
                            {columns.map(col => (
                                <th
                                    key={col.key}
                                    className={`px-3 py-2 text-[9px] font-mono font-semibold text-slate-500 uppercase tracking-widest ${
                                        col.align === 'center' ? 'text-center' : 'text-left'
                                    }`}
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/30">
                        {roundData.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="px-4 py-8 text-center text-[11px] font-mono text-slate-600">
                                    No AFL bets recorded yet
                                </td>
                            </tr>
                        ) : (
                            <>
                                {roundData.map(row => renderRow(row))}
                                {renderRow(totals, true)}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default RoundBreakdown;
